import { Dialog, Empty, ErrorState, Loading, PageHead, Select, useFormat } from '@ly360/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, Plus, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Field } from '../crm/forms';
import { PersonPicker } from '../crm/pickers';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { NewCampaignDialog, type CampaignPreset } from './CampaignForm';
import { RETAIL_KEYS, shelfPrice, useStores, type Product } from './data';
import { DialogButtons, Tabs } from './ui';

const CATEGORIES = ['fragrance', 'bakhoor', 'oud_oil', 'gift_set', 'body_care', 'home', 'other'] as const;
const FAMILIES = [
  'oud',
  'woody',
  'oriental',
  'floral',
  'fresh',
  'citrus',
  'fruity',
  'aromatic',
  'fougere',
  'gourmand',
];

export function StockBadge({ stock }: { stock: number | null }) {
  const { t } = useTranslation();
  if (stock === null) return <span className="faint">—</span>;
  if (stock === 0) return <span className="badge badge-danger">{t('retail.stock.out')}</span>;
  if (stock <= 5)
    return <span className="badge badge-warning">{t('retail.stock.low', { count: stock })}</span>;
  return <span className="badge badge-neutral">{t('retail.stock.left', { count: stock })}</span>;
}

function Price({ p }: { p: Pick<Product, 'priceMinor' | 'salePriceMinor' | 'currency'> }) {
  const fmt = useFormat();
  return (
    <span className="price">
      <strong>{fmt.money(shelfPrice(p), p.currency)}</strong>
      {p.salePriceMinor && Number(p.salePriceMinor) < Number(p.priceMinor) && (
        <s className="faint">{fmt.money(Number(p.priceMinor), p.currency)}</s>
      )}
    </span>
  );
}

export function ProductsPage() {
  const { t, i18n } = useTranslation();
  const fmt = useFormat();
  const { can, session } = useAuth();
  const [tab, setTab] = useState<'catalogue' | 'stores'>('catalogue');
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const [brand, setBrand] = useState('');
  const [stock, setStock] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const params = new URLSearchParams(
    Object.entries({ q, category, brand, stock, limit: '500' }).filter(([, v]) => v),
  );
  const list = useQuery({
    queryKey: ['products', q, category, brand, stock],
    queryFn: () => api<{ items: Product[]; brands: string[] }>(`/v1/products?${params}`),
  });
  const stores = useStores();
  const ar = i18n.language === 'ar';

  return (
    <>
      <PageHead
        eyebrow={t('nav.sales')}
        title={t('retail.products.title')}
        subtitle={t('retail.products.subtitle')}
        actions={
          can('commerce:manage') && (
            <button className="btn btn-grad" onClick={() => setAdding(true)} data-testid="add-product">
              <Plus size={16} />
              {t('retail.products.new')}
            </button>
          )
        }
      />
      <Tabs
        label={t('retail.products.title')}
        value={tab}
        onChange={setTab}
        tabs={[
          { key: 'catalogue', label: t('retail.products.catalogue') },
          { key: 'stores', label: t('retail.products.stores'), count: stores.data?.items.length },
        ]}
      />
      {tab === 'catalogue' && (
        <section className="card">
          <div className="toolbar">
            <input
              className="input"
              type="search"
              placeholder={t('retail.products.search')}
              aria-label={t('common.search')}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <Select
              value={category}
              onChange={setCategory}
              aria-label={t('retail.products.category')}
              options={[
                { value: '', label: t('retail.products.allCategories') },
                ...CATEGORIES.map((c) => ({ value: c, label: t(`retail.category.${c}`) })),
              ]}
            />
            <Select
              value={brand}
              onChange={setBrand}
              aria-label={t('retail.products.brand')}
              options={[
                { value: '', label: t('retail.products.allBrands') },
                ...(list.data?.brands ?? []).map((b) => ({ value: b, label: b })),
              ]}
            />
            <Select
              value={stock}
              onChange={setStock}
              aria-label={t('retail.products.stock')}
              options={[
                { value: '', label: t('retail.products.anyStock') },
                { value: 'in', label: t('retail.products.inStock') },
                { value: 'low', label: t('retail.products.lowStock') },
                { value: 'out', label: t('retail.stock.out') },
              ]}
            />
          </div>
          {list.isLoading && <Loading />}
          {list.isError && <ErrorState error={list.error} />}
          {list.data?.items.length === 0 && <Empty text={t('retail.products.empty')} />}
          {!!list.data?.items.length && (
            <div className="table-wrap">
              <table className="table table-responsive" data-testid="products-table">
                <thead>
                  <tr>
                    <th>{t('retail.col.product')}</th>
                    <th>{t('retail.col.type')}</th>
                    <th className="num">{t('retail.col.price')}</th>
                    <th>{t('retail.col.stock')}</th>
                    <th className="num">{t('retail.col.sold30')}</th>
                    <th className="num">{t('retail.col.waiting')}</th>
                  </tr>
                </thead>
                <tbody>
                  {list.data.items.map((p) => (
                    <tr key={p.id} onClick={() => setOpenId(p.id)} style={{ cursor: 'pointer' }}>
                      <td data-label={t('retail.col.product')}>
                        <button type="button" className="link-btn" onClick={() => setOpenId(p.id)} dir="auto">
                          <strong>{p.brand}</strong> {p.name}
                        </button>
                        <span className="faint" style={{ display: 'block', fontSize: 'var(--t-xs)' }}>
                          {[
                            p.sizeMl && `${p.sizeMl} ml`,
                            p.concentration && t(`retail.concentration.${p.concentration}`),
                            p.sku,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                          {p.ownBrand && (
                            <span className="badge" style={{ marginInlineStart: 6 }}>
                              {t('retail.products.ownBrand')}
                            </span>
                          )}
                        </span>
                      </td>
                      <td data-label={t('retail.col.type')}>
                        {t(`retail.category.${p.category}`)}
                        {p.scentFamily && (
                          <span className="faint"> · {t(`retail.family.${p.scentFamily}`)}</span>
                        )}
                      </td>
                      <td data-label={t('retail.col.price')} className="num">
                        <Price p={p} />
                      </td>
                      <td data-label={t('retail.col.stock')}>
                        <StockBadge stock={p.stock} />
                      </td>
                      <td data-label={t('retail.col.sold30')} className="num">
                        {fmt.number(p.sold30 ?? 0)}
                      </td>
                      <td data-label={t('retail.col.waiting')} className="num">
                        {p.waiting ? (
                          <span className="badge badge-warning">{fmt.number(p.waiting)}</span>
                        ) : (
                          <span className="faint">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
      {tab === 'stores' && (
        <section className="card">
          {stores.isLoading && <Loading />}
          {stores.data && (
            <div className="table-wrap">
              <table className="table table-responsive" data-testid="stores-table">
                <thead>
                  <tr>
                    <th>{t('retail.col.store')}</th>
                    <th>{t('retail.col.area')}</th>
                    <th>{t('retail.col.phone')}</th>
                    <th>{t('retail.col.hours')}</th>
                    <th className="num">{t('retail.col.sales30')}</th>
                  </tr>
                </thead>
                <tbody>
                  {stores.data.items.map((s) => (
                    <tr key={s.id}>
                      <td data-label={t('retail.col.store')} dir="auto">
                        <strong>{(ar && s.nameAr) || s.name}</strong>
                        <span className="faint mono" style={{ display: 'block', fontSize: 'var(--t-xs)' }}>
                          {s.code}
                        </span>
                      </td>
                      <td data-label={t('retail.col.area')}>{[s.area, s.city].filter(Boolean).join(', ')}</td>
                      <td data-label={t('retail.col.phone')} dir="ltr">
                        {s.phone}
                      </td>
                      <td data-label={t('retail.col.hours')} className="faint">
                        {s.hours}
                      </td>
                      <td data-label={t('retail.col.sales30')} className="num">
                        {fmt.money(Number(s.sales30Minor), session!.tenant.currency)}
                        <span className="faint" style={{ display: 'block', fontSize: 'var(--t-xs)' }}>
                          {t('retail.growth.ordersCount', { count: s.orders30 })}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
      {openId && <ProductDialog id={openId} onClose={() => setOpenId(null)} />}
      {adding && <NewProductDialog onClose={() => setAdding(false)} />}
    </>
  );
}

interface ProductDetail {
  product: Product;
  waitlist: {
    id: string;
    personId: string;
    personName: string;
    channel: string | null;
    createdAt: string;
    notifiedAt: string | null;
  }[];
  sales: { units: number; revenueMinor: string; buyers: number };
}

function ProductDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const { can } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['products', 'detail', id],
    queryFn: () => api<ProductDetail>(`/v1/products/${id}`),
  });
  const [person, setPerson] = useState('');
  const [stockEdit, setStockEdit] = useState<string | null>(null);
  const [campaign, setCampaign] = useState<CampaignPreset | null>(null);
  const refresh = () => {
    for (const k of RETAIL_KEYS) void qc.invalidateQueries({ queryKey: [k] });
  };
  const addWait = useMutation({
    mutationFn: (personId: string) =>
      api(`/v1/products/${id}/waitlist`, { method: 'POST', body: JSON.stringify({ personId }) }),
    onSuccess: () => {
      setPerson('');
      refresh();
    },
  });
  const removeWait = useMutation({
    mutationFn: (personId: string) => api(`/v1/products/${id}/waitlist/${personId}`, { method: 'DELETE' }),
    onSuccess: refresh,
  });
  const saveStock = useMutation({
    mutationFn: (stock: number) =>
      api(`/v1/products/${id}`, { method: 'PATCH', body: JSON.stringify({ stock }) }),
    onSuccess: () => {
      setStockEdit(null);
      refresh();
    },
  });
  const p = q.data?.product;
  const waiting = (q.data?.waitlist ?? []).filter((w) => !w.notifiedAt);
  return (
    <Dialog
      open
      wide
      title={p ? `${p.brand} ${p.name}` : t('common.loading')}
      onClose={onClose}
      testId="product-dialog"
    >
      {q.isLoading && <Loading />}
      {q.isError && <ErrorState error={q.error} />}
      {p && q.data && (
        <div className="product-detail">
          <div className="stat-grid">
            <div className="stat">
              <div className="label">{t('retail.col.price')}</div>
              <div className="value">
                <Price p={p} />
              </div>
            </div>
            <div className="stat">
              <div className="label">{t('retail.col.stock')}</div>
              <div className="value">
                {stockEdit === null ? (
                  <>
                    <StockBadge stock={p.stock} />
                    {can('commerce:manage') && (
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => setStockEdit(String(p.stock ?? 0))}
                        data-testid="edit-stock"
                      >
                        {t('common.edit')}
                      </button>
                    )}
                  </>
                ) : (
                  <form
                    className="inline-form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      saveStock.mutate(Number(stockEdit));
                    }}
                  >
                    <input
                      className="input"
                      type="number"
                      min={0}
                      aria-label={t('retail.col.stock')}
                      value={stockEdit}
                      onChange={(e) => setStockEdit(e.target.value)}
                      data-testid="stock-input"
                    />
                    <button className="btn btn-sm btn-grad" type="submit">
                      {t('common.save')}
                    </button>
                  </form>
                )}
              </div>
            </div>
            <div className="stat">
              <div className="label">{t('retail.products.soldYear')}</div>
              <div className="value">{fmt.number(q.data.sales.units)}</div>
              <div className="stat-note faint">
                {fmt.money(Number(q.data.sales.revenueMinor), p.currency)} ·{' '}
                {t('retail.products.buyers', { count: q.data.sales.buyers })}
              </div>
            </div>
          </div>
          <dl className="kv" style={{ marginTop: '1rem' }}>
            <dt>{t('retail.col.type')}</dt>
            <dd>
              {t(`retail.category.${p.category}`)}
              {p.gender && ` · ${t(`retail.gender.${p.gender}`)}`}
              {p.concentration && ` · ${t(`retail.concentration.${p.concentration}`)}`}
              {p.sizeMl && ` · ${p.sizeMl} ml`}
            </dd>
            {p.scentFamily && (
              <>
                <dt>{t('retail.products.family')}</dt>
                <dd>
                  {t(`retail.family.${p.scentFamily}`)}
                  {p.notes.length > 0 && <span className="faint"> · {p.notes.join(', ')}</span>}
                </dd>
              </>
            )}
            {p.nameAr && (
              <>
                <dt>{t('retail.products.nameAr')}</dt>
                <dd dir="rtl" lang="ar">
                  {p.nameAr}
                </dd>
              </>
            )}
            <dt>SKU</dt>
            <dd className="mono">{p.sku}</dd>
          </dl>
          <h3 className="section-title" style={{ marginTop: '1.25rem' }}>
            {t('retail.products.waitlist', { count: waiting.length })}
          </h3>
          <p className="faint" style={{ marginTop: 0 }}>
            {t('retail.products.waitlistHint')}
          </p>
          {waiting.length > 0 && (
            <ul className="list-plain" data-testid="waitlist">
              {waiting.map((w) => (
                <li key={w.id}>
                  <Link className="grow" to={`/people/${w.personId}`} dir="auto">
                    {w.personName}
                  </Link>
                  <span className="faint">{fmt.relative(w.createdAt)}</span>
                  {can('commerce:write') && (
                    <button
                      className="icon-btn"
                      onClick={() => removeWait.mutate(w.personId)}
                      aria-label={t('retail.products.removeWaiting', { name: w.personName })}
                    >
                      <X size={14} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {can('commerce:write') && (
            <div className="inline-form" style={{ marginTop: '0.5rem' }}>
              <div className="grow">
                <PersonPicker id="pd-wait" value={person} onChange={setPerson} />
              </div>
              <button
                className="btn btn-sm"
                disabled={!person || addWait.isPending}
                onClick={() => addWait.mutate(person)}
                data-testid="add-waiting"
              >
                <Bell size={14} />
                {t('retail.products.addWaiting')}
              </button>
            </div>
          )}
          {!!p.stock && waiting.length > 0 && can('campaigns:manage') && (
            <div className="notice success" style={{ marginTop: '1rem' }}>
              <span className="grow">{t('retail.products.backInStock', { count: waiting.length })}</span>
              <button
                className="btn btn-sm btn-grad"
                onClick={() =>
                  setCampaign({
                    name: t('retail.opp.backInStockName', { product: `${p.brand} ${p.name}` }),
                    segmentName: t('retail.opp.backInStockName', { product: `${p.brand} ${p.name}` }),
                    rules: [{ type: 'waitlist', productIds: [p.id] }],
                    templateName: 'back_in_stock',
                  })
                }
                data-testid="notify-waiting"
              >
                {t('retail.opp.notify', { count: waiting.length })}
              </button>
            </div>
          )}
        </div>
      )}
      {campaign && <NewCampaignDialog open preset={campaign} onClose={() => setCampaign(null)} />}
    </Dialog>
  );
}

function NewProductDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [f, setF] = useState({
    sku: '',
    brand: '',
    name: '',
    nameAr: '',
    category: 'fragrance',
    gender: '',
    concentration: '',
    sizeMl: '',
    scentFamily: '',
    price: '',
    salePrice: '',
    stock: '',
  });
  const set = (p: Partial<typeof f>) => setF((x) => ({ ...x, ...p }));
  const m = useMutation({
    mutationFn: () =>
      api('/v1/products', {
        method: 'POST',
        body: JSON.stringify({
          sku: f.sku,
          brand: f.brand,
          name: f.name,
          nameAr: f.nameAr || null,
          category: f.category,
          gender: f.gender || null,
          concentration: f.concentration || null,
          sizeMl: f.sizeMl ? Number(f.sizeMl) : null,
          scentFamily: f.scentFamily || null,
          price: Number(f.price),
          salePrice: f.salePrice ? Number(f.salePrice) : null,
          stock: f.stock === '' ? null : Number(f.stock),
        }),
      }),
    onSuccess: () => {
      for (const k of RETAIL_KEYS) void qc.invalidateQueries({ queryKey: [k] });
      onClose();
    },
  });
  const text = (k: keyof typeof f, label: string, extra: Record<string, unknown> = {}) => (
    <Field id={`np-${k}`} label={label}>
      <input
        id={`np-${k}`}
        className="input"
        value={f[k]}
        onChange={(e) => set({ [k]: e.target.value })}
        {...extra}
      />
    </Field>
  );
  return (
    <Dialog
      open
      wide
      title={t('retail.products.new')}
      onClose={onClose}
      onSubmit={() => m.mutate()}
      testId="new-product"
      footer={<DialogButtons onClose={onClose} busy={m.isPending} label={t('common.create')} />}
    >
      <div className="form-grid">
        {text('brand', t('retail.products.brand'), { required: true })}
        {text('name', t('retail.products.name'), { required: true })}
        {text('nameAr', t('retail.products.nameAr'), { dir: 'rtl', lang: 'ar' })}
        {text('sku', 'SKU', { required: true, dir: 'ltr', className: 'input mono' })}
        <Field id="np-category" label={t('retail.products.category')}>
          <Select
            id="np-category"
            value={f.category}
            onChange={(v) => set({ category: v })}
            options={CATEGORIES.map((c) => ({ value: c, label: t(`retail.category.${c}`) }))}
          />
        </Field>
        <Field id="np-gender" label={t('retail.products.gender')}>
          <Select
            id="np-gender"
            value={f.gender}
            onChange={(v) => set({ gender: v })}
            options={[
              { value: '', label: t('common.none') },
              ...(['women', 'men', 'unisex'] as const).map((g) => ({
                value: g,
                label: t(`retail.gender.${g}`),
              })),
            ]}
          />
        </Field>
        <Field id="np-conc" label={t('retail.products.concentration')}>
          <Select
            id="np-conc"
            value={f.concentration}
            onChange={(v) => set({ concentration: v })}
            options={[
              { value: '', label: t('common.none') },
              ...(['parfum', 'extrait', 'edp', 'edt', 'cologne', 'elixir'] as const).map((c) => ({
                value: c,
                label: t(`retail.concentration.${c}`),
              })),
            ]}
          />
        </Field>
        <Field id="np-family" label={t('retail.products.family')}>
          <Select
            id="np-family"
            value={f.scentFamily}
            onChange={(v) => set({ scentFamily: v })}
            options={[
              { value: '', label: t('common.none') },
              ...FAMILIES.map((x) => ({ value: x, label: t(`retail.family.${x}`) })),
            ]}
          />
        </Field>
        {text('sizeMl', t('retail.products.size'), { type: 'number', min: 1, inputMode: 'numeric' })}
        {text('price', t('retail.products.price'), {
          type: 'number',
          min: 0,
          step: 'any',
          required: true,
          inputMode: 'decimal',
        })}
        {text('salePrice', t('retail.products.salePrice'), {
          type: 'number',
          min: 0,
          step: 'any',
          inputMode: 'decimal',
        })}
        {text('stock', t('retail.col.stock'), { type: 'number', min: 0, inputMode: 'numeric' })}
      </div>
      {m.isError && <ErrorState error={m.error} />}
    </Dialog>
  );
}
